import { forwardRef, useEffect, useState } from 'react';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';
import Link from '@zenbusiness/zui/Link';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { VeloCtaRow } from '#client/components/VeloCtaRow';
import { isAccessedFromVeloChat } from '#client/pages/ProductLanding/utils/isAccessedFromVeloChat';

import * as Styled from './BuyBox.style';
import { BuyBoxProvider, useBuyBoxContext } from './BuyBoxContext';
import CTAButton from './CTAButton';
import Features from './Features';
import TitleSection from './TitleSection';
import VeloBanner from './VeloBanner';

import { useVeloBannerFeatureFlag } from '../../utils/veloBannerFlags';
import { Callout } from '../Callout';
import { ComparePackagesModal } from '../ComparePackagesModal';

const BuyBoxContent = forwardRef(function BuyBoxContent(props, ref) {
  const {
    productId,
    note,
    noteLink,
    packages,
    packageActive,
    comparePackages,
    plans,
    bulletPoints,
    bulletPointsBoldWeight,
    elevatedCard,
    packageNote,
    productDescription,
    packageLabel,
    packageColor,
    displayCallout,
    displayCouponBanner,
    couponBannerMessage,
    termsLink,
    veloBanner,
    veloCta,
    handleTabChange,
    handleComparePackagesClick,
    textLink,
    handleTextLinkClick,
  } = useBuyBoxContext();

  const isVeloAmendmentSlideOutEnabled = useVeloBannerFeatureFlag();

  // document.referrer is unavailable during SSR (isAccessedFromVeloChat returns false),
  // so this check is deferred to a client-only effect instead of running at render time —
  // otherwise the client's first render could disagree with the server's, causing a
  // hydration mismatch.
  const [isFromVeloChat, setIsFromVeloChat] = useState(false);

  useEffect(() => {
    setIsFromVeloChat(isAccessedFromVeloChat());
  }, []);

  return (
    <Styled.BuyBoxWrapper>
      <Styled.BuyBoxContainer ref={ref} elevated={elevatedCard}>
        {packages.length > 1 && (
          <Styled.TabsContainer
            onChange={handleTabChange}
            scrollButtons="auto"
            value={packageActive}
            variant="scrollable"
          >
            {packages.map((pkg, index) => {
              const active = packageActive === index;
              return (
                <Styled.TabButton
                  active={packageActive.toString()}
                  key={pkg.packageName}
                  label={
                    <Stack>
                      <Text color={active ? 'white' : 'primary'}>
                        <FormattedMessage id={pkg.packageName} />
                      </Text>
                      {pkg.packageNote && (
                        <Text color={active ? 'white' : 'primary'} size="xs">
                          <FormattedMessage id={pkg.packageNote} />
                        </Text>
                      )}
                    </Stack>
                  }
                  selected={active}
                  value={index}
                />
              );
            })}
          </Styled.TabsContainer>
        )}

        <Styled.BuyBox note={packageNote} color={packageColor}>
          {packageNote && (
            <Styled.PackageNoteLabel color="primary" size="xs" textTransform="uppercase" weight="medium">
              <FormattedMessage id={packageLabel || packageNote} />
            </Styled.PackageNoteLabel>
          )}
          <Styled.BuyBoxContent>
            <TitleSection />
            <Divider />
            {productDescription && (
              <Text color="primary" weight="medium">
                <FormattedMessage id={productDescription} />
              </Text>
            )}
            <Features features={bulletPoints} boldWeight={bulletPointsBoldWeight} />
            {displayCallout && <Callout title="productPage.buyBox.purchasedCallout.title" variant="info" />}
            {displayCouponBanner && (
              <Styled.StyledCouponBanner
                title=""
                subtitle={<FormattedMessage id={couponBannerMessage} />}
                variant="promo"
              />
            )}
            {veloBanner?.displayVeloBanner && isVeloAmendmentSlideOutEnabled && (
              <VeloBanner prompt={veloBanner.prompt} message={veloBanner.message} queryType={veloBanner.queryType} />
            )}
            {veloCta?.displayVeloCta && !isFromVeloChat && (
              <VeloCtaRow
                topic={veloCta.topic}
                promptMessageId={veloCta.promptMessageId}
                buttonMessageId="dashboard.orderTracker.velo.askVeloButton"
                showAskVeloLabel={false}
                isGradientButton
                elementName="product_landing_ask_velo"
                elementText="Ask Velo"
                pageSlug={productId}
                elementPosition="buy_box"
              />
            )}
            <CTAButton />
            {termsLink && (
              <Stack alignItems="flex-end">
                <Link href={termsLink.url} size="sm" color="info" underline="none">
                  <FormattedMessage id={termsLink.message} />
                </Link>
              </Stack>
            )}
            {note && (
              <Styled.SmallCopyText>
                <FormattedMessage id={note} />{' '}
                {noteLink && (
                  <Link
                    href={noteLink.url}
                    size="sm"
                    color="info"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                  >
                    <FormattedMessage id={noteLink.text} />
                  </Link>
                )}
              </Styled.SmallCopyText>
            )}
            {textLink && (
              <Styled.ButtonLink onClick={handleTextLinkClick} size="sm" variant="text" color="info" p={0}>
                <FormattedMessage id={textLink} />
              </Styled.ButtonLink>
            )}
            {comparePackages && (
              <Styled.ButtonLinkContainer>
                <Styled.ComparePackagesButton
                  onClick={handleComparePackagesClick}
                  size="sm"
                  variant="text"
                  color="info"
                >
                  <FormattedMessage id={comparePackages} />
                </Styled.ComparePackagesButton>
              </Styled.ButtonLinkContainer>
            )}
            {plans && <ComparePackagesModal plans={plans} />}
          </Styled.BuyBoxContent>
        </Styled.BuyBox>
      </Styled.BuyBoxContainer>
    </Styled.BuyBoxWrapper>
  );
});

const BuyBox = forwardRef(function BuyBox(props, ref) {
  return (
    <BuyBoxProvider>
      <BuyBoxContent ref={ref} />
    </BuyBoxProvider>
  );
});

export default BuyBox;
